//dependencies
import { format } from "date-fns";
import React from "react";
import { useParams } from "react-router-dom";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";

import { AvatarProfile, Text } from "@performyard/py-components";

import VerticalExpander from "../../../../../components/AnimationWrappers/VerticalExpander";
import Button from "../../../../../components/Button";
import RotatingIconButton from "../../../../../components/Button/templates/RotatingIconButton";
import { Divider } from "../../../../../components/Divider";
//components
import Spacer from "../../../../../components/Spacer";
import GoalProgressDoughnutChart from "./components/GoalProgressDoughnutChart";
import GoalProgressLineChart from "./components/GoalProgressLineChart";

//services
import { useGoalDetails } from "../../../../../api/hooks";
//hooks
import useSavedPageState from "../../../../../hooks/usePageSavedPageState";
import usePageWidthBreakpoint from "../../../../../hooks/usePageWidthBreakpoint";

import { Goal } from "../../../../../api/services";

import { getProgressLabels } from "./utils";

//config
import Config from "../../config";
//css
import css from "./SingleGoalInfo.module.scss";

const SingleGoalInfo = () => {
	const { goal_id: goalId } = useParams<{ goal_id: string }>();

	const { isTablet, isMobile } = usePageWidthBreakpoint();
	const useMobileLayout = isMobile || isTablet;
	const {
		pageState: { chartsHidden },
		updatePageState,
	} = useSavedPageState({
		pageName: "goal_progress",
		schema: Config.savedQueryParametersSchema,
	});

	const toggleHideCharts = (value) => {
		updatePageState({ chartsHidden: value ? undefined : true });
	};

	const { data } = useGoalDetails({ goalID: goalId || "" });
	const goal = data ? data.goal : ({} as Goal);

	const status = goal?.status === "done" ? "completed" : goal?.status;

	const isChartsExpanded = !chartsHidden;

	const targetLabels =
		goal?.target !== null && goal?.target !== undefined
			? getProgressLabels({ value: goal?.target, units: goal?.units, decimalPlacesOverride: 2 })
			: {};

	const formattedTarget = `${targetLabels?.mainText || ""} ${targetLabels?.subText || ""}`;

	return (
		<div style={{ maxWidth: "100%" }}>
			<div className={css.infoRow}>
				{!useMobileLayout && (
					<RotatingIconButton
						reverseDirection
						icon={faChevronUp}
						onClick={() => {
							toggleHideCharts(!isChartsExpanded);
						}}
						aria-label="Toggle charts"
					/>
				)}
				<div className={`${css.infoContainer} ${useMobileLayout ? css.mobile : ""}`}>
					{goal?.assignee && (
						<div className={css.info}>
							<div className="shrink-0">
								<Text size="paragraph-3" weight="semi-bold">
									Assignee:{" "}
								</Text>
							</div>
							<AvatarProfile
								employeeId={goal?.assignee?._id}
								avatarSize={24}
								employeeName={goal?.assignee?.name}
								textSize="paragraph-3"
								textWeight="regular"
								avatarTestId={`avatar-${goal?.assignee?._id}`}
							/>
						</div>
					)}

					<div className={css.info}>
						<div className="shrink-0">
							<Text size="paragraph-3" weight="semi-bold">
								Target:{" "}
							</Text>
						</div>
						<Text size="paragraph-3" weight="regular">
							{formattedTarget}
						</Text>
					</div>

					{goal?.start_date && (
						<div className={css.info}>
							<div className="shrink-0">
								<Text size="paragraph-3" weight="semi-bold">
									Start Date:{" "}
								</Text>
							</div>
							<Text size="paragraph-3" weight="regular">
								{goal?.start_date && format(goal?.start_date, "MMM d yyyy")}
							</Text>
						</div>
					)}
					{goal?.end_date && (
						<div className={css.info}>
							<div className="shrink-0">
								<Text size="paragraph-3" weight="semi-bold">
									Due Date:{" "}
								</Text>
							</div>

							<Text size="paragraph-3" weight="regular">
								{goal?.end_date && format(goal?.end_date, "MMM d yyyy")}
							</Text>
						</div>
					)}
					{goal?.status && (
						<div className={css.info}>
							<div className="shrink-0">
								<Text size="paragraph-3" weight="semi-bold">
									Status:{" "}
								</Text>
							</div>
							<Text size="paragraph-3" weight="regular">
								{status && status.charAt(0).toUpperCase() + status.slice(1)}
							</Text>
						</div>
					)}
					{goal?.categories && goal?.categories?.length > 0 && (
						<div className={css.info}>
							<div className="shrink-0">
								<Text size="paragraph-3" weight="semi-bold">
									Categories:{" "}
								</Text>
							</div>
							<Text size="paragraph-3" weight="regular">
								{goal?.categories?.map((category) => category.name).join(", ")}
							</Text>
						</div>
					)}
				</div>
			</div>
			{useMobileLayout ? (
				<Button
					className={css.mobileViewButton}
					isHollow={true}
					iconPosition="right"
					icon={isChartsExpanded ? faChevronDown : faChevronUp}
					onClick={(event) => {
						event?.preventDefault();
						toggleHideCharts(!isChartsExpanded);
					}}
				>
					{isChartsExpanded ? "Hide Graphs" : "Show Graphs"}
				</Button>
			) : (
				<Divider />
			)}
			<VerticalExpander isExpanded={!chartsHidden} className={css.graphsContainer}>
				<div className={css.graphs}>
					<GoalProgressDoughnutChart />
					<GoalProgressLineChart />
				</div>
				<Spacer height={1} />
				<Divider />
			</VerticalExpander>
		</div>
	);
};

export default SingleGoalInfo;
